import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Lock, ShieldHalf, ArrowLeft } from "lucide-react";

const LEN = 4;

export default function AdminGate({ children }) {
  const { adminUnlocked, adminUnlock } = useAuth();
  const nav = useNavigate();
  const [digits, setDigits] = useState(Array(LEN).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const refs = useRef([]);

  if (adminUnlocked) return children;

  const submit = async (code) => {
    setLoading(true);
    setError("");
    try {
      await adminUnlock(code);
      // adminUnlocked flips true; component re-renders children
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
      setDigits(Array(LEN).fill(""));
      refs.current[0]?.focus();
      setLoading(false);
    }
  };

  const onChange = (i, v) => {
    const val = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    if (val && i < LEN - 1) refs.current[i + 1]?.focus();
    if (next.every((d) => d !== "")) submit(next.join(""));
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <button data-testid="admin-gate-back" onClick={() => nav("/")} className="flex items-center gap-2 text-muted-foreground hover:text-white text-xs overline mb-6">
          <ArrowLeft size={16} /> Back to site
        </button>
        <div className="bg-card border-2 border-primary p-8 block-shadow">
          <ShieldHalf className="text-primary mb-4" size={40} />
          <div className="overline text-xs text-accent mb-2">Restricted</div>
          <h1 className="font-display text-3xl font-bold mb-2">CONTROL ROOM</h1>
          <p className="text-sm text-muted-foreground mb-6">Enter the security code to access the admin panel.</p>

          {error && <div data-testid="admin-gate-error" className="bg-primary/10 border border-primary text-primary text-sm p-3 mb-4">{error}</div>}

          <div className="flex gap-3 justify-center mb-6">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (refs.current[i] = el)}
                data-testid={`admin-code-${i}`}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={d}
                disabled={loading}
                onChange={(e) => onChange(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                autoFocus={i === 0}
                className="w-14 h-16 text-center text-2xl font-display font-bold bg-transparent border-2 border-border focus:border-primary outline-none text-white transition-colors"
              />
            ))}
          </div>

          <Button
            data-testid="admin-gate-submit"
            onClick={() => submit(digits.join(""))}
            disabled={loading || digits.some((d) => d === "")}
            className="w-full h-12 bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline"
          >
            <Lock size={16} className="mr-2" /> {loading ? "Verifying..." : "Unlock"}
          </Button>
          <p className="text-[11px] text-muted-foreground mt-4 text-center">Access clears when you close the tab.</p>
        </div>
      </div>
    </div>
  );
}
